import { PROVIDERS, type Schedule } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Pause, Play, Plus, Trash2, Zap } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { schedulesApi } from "@/api/browser.ts";
import { accountsApi } from "@/api/accounts.ts";
import { chatLink } from "@/features/shell/ProjectsSidebar.tsx";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";
import { cn } from "@/lib/cn.ts";
import { Alert, Button, Field, Input, Select } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

const PRESETS: { label: string; cron: string }[] = [
  { label: "every hour", cron: "0 * * * *" },
  { label: "daily 09:00", cron: "0 9 * * *" },
  { label: "daily 03:00", cron: "0 3 * * *" },
  { label: "weekdays 08:30", cron: "30 8 * * 1-5" },
  { label: "every 15 min", cron: "*/15 * * * *" },
];

/**
 * Scheduled tasks: one-time or cron prompts that run in a chat of this project while you are away.
 * `chatSection`: the Chat section's tasks, which ask the providers ticked for each task (a providers conversation).
 */
export function SchedulesPanel({ projectId, canEdit, chatSection = false }: { projectId: string; canEdit: boolean; chatSection?: boolean }) {
  const { t, i18n } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["schedules", projectId], queryFn: () => schedulesApi.list(projectId), refetchInterval: 20_000 });
  const [creating, setCreating] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [form, setForm] = useState({ name: "", prompt: "", kind: "cron" as "cron" | "once", cron: "0 9 * * *", runAt: "", timezone: tz, maxRuns: "" });
  const refresh = () => qc.invalidateQueries({ queryKey: ["schedules", projectId] });
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list, staleTime: 60_000, enabled: chatSection });
  const connected = (accounts.data ?? []).filter((a) => a.status === "connected");
  const [picked, setPicked] = useState<string[] | null>(null);
  const providers = picked ?? connected.filter((a) => a.provider === "claude").slice(0, 1).map((a) => a.id);
  const togglePick = (id: string) => setPicked(providers.includes(id) ? (providers.length > 1 ? providers.filter((x) => x !== id) : providers) : [...providers, id]);
  const create = useMutation({
    mutationFn: () => schedulesApi.create(projectId, { name: form.name, prompt: form.prompt, kind: form.kind, timezone: form.timezone, active: false, ...(chatSection && providers.length ? { accountIds: providers } : {}), ...(form.kind === "cron" ? { cron: form.cron } : { runAt: new Date(form.runAt).toISOString() }), ...(form.maxRuns ? { maxRuns: Number(form.maxRuns) } : {}) }),
    onSuccess: () => { setCreating(false); setForm({ ...form, name: "", prompt: "" }); void refresh(); },
  });
  const toggle = useMutation({ mutationFn: (s: Schedule) => schedulesApi.update(projectId, s.id, { active: s.status !== "active" }), onSuccess: refresh });
  const run = useMutation({ mutationFn: (s: Schedule) => schedulesApi.run(projectId, s.id), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (s: Schedule) => schedulesApi.remove(projectId, s.id), onSuccess: refresh });
  const { confirm } = useConfirm();
  const askRemove = async (s: Schedule) => { if (await confirm({ title: t("confirm.deleteScheduleTitle"), message: t("confirm.deleteScheduleMsg", { name: s.name }), confirmLabel: t("common.delete"), danger: true })) remove.mutate(s); };
  const fmt = (iso: string | null) => (iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso)) : "—");
  const err = create.error ?? toggle.error ?? run.error ?? remove.error;

  return (
    <div className="flex h-full flex-col text-sm">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="text-xs text-fg-muted">{t("schedules.intro")}</span>
        {canEdit && <Button size="sm" variant={creating ? "secondary" : "primary"} onClick={() => setCreating(!creating)}><Plus className="size-3.5" /> {t("schedules.new")}</Button>}
      </div>
      {err && <div className="p-2"><Alert tone="danger">{toMsg(err)}</Alert></div>}
      {creating && (
        <form className="space-y-3 border-b border-border p-3" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <Field label={t("schedules.name")}><Input required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label={t("schedules.prompt")}>
            <textarea required className="min-h-20 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("schedules.kind")}>
              <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as "cron" | "once" })}>
                <option value="cron">{t("schedules.recurring")}</option>
                <option value="once">{t("schedules.once")}</option>
              </Select>
            </Field>
            <Field label={t("schedules.timezone")}><Input dir="ltr" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></Field>
          </div>
          {form.kind === "cron" ? (
            <Field label={t("schedules.cron")} hint={t("schedules.cronHint")}>
              <div className="flex flex-wrap gap-1 pb-1">{PRESETS.map((p) => <button type="button" key={p.cron} className={cn("rounded border px-1.5 py-0.5 text-[11px]", form.cron === p.cron ? "border-accent bg-accent-soft" : "border-border hover:bg-surface-2")} onClick={() => setForm({ ...form, cron: p.cron })}>{p.label}</button>)}</div>
              <Input dir="ltr" className="font-mono" required value={form.cron} onChange={(e) => setForm({ ...form, cron: e.target.value })} />
            </Field>
          ) : (
            <Field label={t("schedules.runAt")}><Input dir="ltr" type="datetime-local" required value={form.runAt} onChange={(e) => setForm({ ...form, runAt: e.target.value })} /></Field>
          )}
          {chatSection && (
            <div className="space-y-1">
              <div className="text-sm font-medium">{t("schedules.providers")}</div>
              <div className="flex flex-wrap gap-1">
                {connected.map((a) => (
                  <button key={a.id} type="button" onClick={() => togglePick(a.id)} className={cn("flex h-8 items-center gap-1.5 rounded-full border px-2.5 text-xs", providers.includes(a.id) ? "border-accent bg-accent-soft" : "border-border text-fg-muted")}><ProviderIcon provider={a.provider} size="xs" /> {PROVIDERS[a.provider].label}</button>
                ))}
              </div>
            </div>
          )}
          <Field label={t("schedules.maxRuns")}><Input dir="ltr" type="number" min={1} value={form.maxRuns} onChange={(e) => setForm({ ...form, maxRuns: e.target.value })} /></Field>
          <div className="flex gap-2">
            <Button type="submit" size="sm" loading={create.isPending}>{t("schedules.create")}</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setCreating(false)}>{t("common.cancel")}</Button>
          </div>
          <p className="text-[11px] text-fg-muted">{t("schedules.pausedHint")}</p>
        </form>
      )}
      <ul className="min-h-0 flex-1 divide-y divide-border overflow-auto">
        {q.data?.length === 0 && !creating && <li className="p-3 text-xs text-fg-muted">{t("schedules.empty")}</li>}
        {(q.data ?? []).map((s) => (
          <li key={s.id} className="space-y-1 p-3">
            <div className="flex items-center gap-2">
              <span className={cn("size-2 rounded-full", s.status === "active" ? "bg-success" : s.status === "error" ? "bg-danger" : s.status === "done" ? "bg-fg-muted" : "bg-warn")} />
              <span className="min-w-0 flex-1 truncate font-medium">{s.name}</span>
              <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-fg-muted">{t(`schedules.status.${s.status}`)}</span>
              {canEdit && (
                <div className="flex gap-0.5">
                  {s.status !== "done" && <button type="button" className="rounded p-1 text-fg-muted hover:text-fg" title={s.status === "active" ? t("schedules.pause") : t("schedules.arm")} onClick={() => toggle.mutate(s)}>{s.status === "active" ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}</button>}
                  <button type="button" className="rounded p-1 text-fg-muted hover:text-fg" title={t("schedules.runNow")} onClick={() => run.mutate(s)}><Zap className="size-3.5" /></button>
                  <button type="button" className="rounded p-1 text-fg-muted hover:text-danger" title={t("common.delete")} onClick={() => void askRemove(s)}><Trash2 className="size-3.5" /></button>
                </div>
              )}
            </div>
            <p className="line-clamp-2 text-xs text-fg-muted">{s.prompt}</p>
            <div className="flex flex-wrap gap-x-3 text-[11px] text-fg-muted">
              <span dir="ltr">{s.kind === "cron" ? `${s.cron} (${s.timezone})` : fmt(s.runAt)}</span>
              <span>{t("schedules.next")}: {fmt(s.nextRunAt)}</span>
              <span>{t("schedules.last")}: {fmt(s.lastRunAt)}</span>
              <span>{t("schedules.runs")}: {s.runs}{s.maxRuns ? `/${s.maxRuns}` : ""}</span>
              {chatSection && s.accountIds && <span className="flex items-center gap-1">{s.accountIds.map((id) => { const a = accounts.data?.find((x) => x.id === id); return a ? <ProviderIcon key={id} provider={a.provider} size="xs" /> : null; })}</span>}
              {s.chatId && <Link {...chatLink({ id: projectId, kind: chatSection ? "personal" : "project" }, s.chatId)} className="text-accent underline">{t("schedules.openChat")}</Link>}
            </div>
            {s.lastResult?.startsWith("error") && <p dir="ltr" className="truncate text-[11px] text-danger">{s.lastResult}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}
